'use client';

import { useShallow } from 'zustand/react/shallow';
import { Pagination } from 'ui/components/Pagination/Pagination';
import { useEffect, useMemo, useState } from 'react';
import { AmplitudeEvents } from 'whoop-analytics/lib/types';
import {
  getCartItemsFromCartItemGroups,
  getMembershipTierFromCart,
  getProductInCart,
} from '../Cart/cart.utils';
import { Header } from './Header';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useGetCurrentPageMaverick } from '@/hooks/getCurrentPage';
import { useNavigation } from '@/hooks/navigation';
import { useAnalytics } from '@/hooks/analytics';
import { isJFStepsPage, isUpgradeStepsPage } from '@/lib/utils/maverickHelpers';
import {
  JOIN_FLOW_STEPS,
  UPGRADE_STEPS,
  ProductType,
  MembershipTierWithTrial,
} from '@/lib/constants/maverick';
import { EXPERIMENT_JF_CART_ITEMS } from '@/lib/constants/experiments';
import { filterCartItemsForExperiment } from '@/lib/utils/parseCart';
import { EXPERIMENT_JF_HIDE_PAGINATION } from '@/lib/constants/experiments';
import { useExperimentService } from '@/hooks/experimentService';

function PaginationHeaderV2({
  experimentUUID,
}: {
  experimentUUID?: string;
}): JSX.Element {
  const { getLocalizedPath } = useNavigation();
  const { trackAmplitudeEvent, setAmplitudeUserProperty } = useAnalytics();
  const { getVariant } = useExperimentService();

  const { toggleIsCartOpen, cart, experimentVariants } = useSiteStore(
    useShallow((state) => ({
      toggleIsCartOpen: state.toggleIsCartOpen,
      cart: state.cart,
      experimentVariants: state.experimentVariants,
    })),
  );
  const cartExperimentVariant = experimentVariants[EXPERIMENT_JF_CART_ITEMS];

  const currentPage = useGetCurrentPageMaverick();
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [visitedJFPages, setVisitedJFPages] = useState({
    bands: false,
    membership: false,
    accessories: false,
  });
  const [visitedUpgradePages, setVisitedUpgradePages] = useState({
    options: false,
    upcycle: false,
    accessories: false,
  });

  useEffect(() => {
    const initializeExperiment = async () => {
      const isUpgradeStep = isUpgradeStepsPage(currentPage);
      if (isUpgradeStep) {
        // flip to true (we needed to initialize state as false to prevent flashing in the UI)
        setShowPagination(true);
      } else {
        if (experimentUUID) {
          // only call experiment service if we are on a join flow step
          const variant = await getVariant(
            EXPERIMENT_JF_HIDE_PAGINATION,
            undefined,
            experimentUUID,
          );
          if (variant) {
            setAmplitudeUserProperty(EXPERIMENT_JF_HIDE_PAGINATION, variant);
          }
          // show pagination if experiment is inactive or if experiment is active and variant is control
          setShowPagination(!variant || variant === 'control');
        }
      }
    };
    initializeExperiment();
  }, [experimentUUID]);

  let tier: MembershipTierWithTrial | undefined;
  // if second step has been visited, save last added tier so user can jump back and forth to the right urls with pagination
  if (
    visitedUpgradePages.options ||
    visitedJFPages.bands ||
    cart.cartItemGroups?.length
  ) {
    tier = getMembershipTierFromCart(cart.cartItemGroups);
  }

  const upgradePaginationOptionsItems = {
    upgrade: {
      label: '1',
      link: getLocalizedPath('upgrade'),
      enabled: true,
    },
    options: {
      label: '2',
      link: tier ? getLocalizedPath(`/upgrade/options/?tier=${tier}`) : '#',
      enabled: false,
    },
    upcycle: {
      label: '3',
      link: getLocalizedPath('upgrade/upcycle'),
      enabled: false,
    },
    accessories: {
      label: '4',
      link: tier ? getLocalizedPath(`/upgrade/accessories/?tier=${tier}`) : '#',
      enabled: false,
    },
  };

  const jfPaginationOptionsItems = {
    join: {
      label: '1',
      link: getLocalizedPath(''),
      enabled: true,
    },
    bands: {
      label: '2',
      link: tier ? getLocalizedPath(`/bands/?tier=${tier}`) : '#',
      enabled: false,
    },
    membership: {
      label: '3',
      link: tier ? getLocalizedPath(`/membership/?tier=${tier}`) : '#',
      enabled: false,
    },
    accessories: {
      label: '4',
      link: tier ? getLocalizedPath(`/accessories/?tier=${tier}`) : '#',
      enabled: false,
    },
  };

  const isMembershipInCart = Boolean(
    getProductInCart(cart.cartItemGroups, ProductType.MEMBERSHIP),
  );
  const isExtensionOrUpgradeFeeInCart = Boolean(
    getProductInCart(cart.cartItemGroups, ProductType.EXTENSION) ||
      getProductInCart(cart.cartItemGroups, ProductType.UPGRADE_FEE),
  );
  const isDeviceInCart = Boolean(
    getProductInCart(cart.cartItemGroups, ProductType.DEVICE),
  );
  const isUpcycleInCart = Boolean(
    getProductInCart(cart.cartItemGroups, ProductType.UPCYCLE_MEMBERSHIP),
  );

  const paginationOptions = useMemo(() => {
    // upgrade flow
    if (isUpgradeStepsPage(currentPage)) {
      const items = [
        upgradePaginationOptionsItems.upgrade,
        upgradePaginationOptionsItems.options,
        upgradePaginationOptionsItems.upcycle,
        upgradePaginationOptionsItems.accessories,
      ];

      // enable step 2 (options page) when device + tier are in cart
      if (
        (isDeviceInCart && currentPage === UPGRADE_STEPS.OPTIONS) ||
        visitedUpgradePages.options
      ) {
        items[1].enabled = true;
        setVisitedUpgradePages((prev) => ({ ...prev, options: true }));
      }
      // enable step 3 (upcycle page) when extension is in cart
      if (
        (isExtensionOrUpgradeFeeInCart &&
          currentPage === UPGRADE_STEPS.UPCYCLE) ||
        visitedUpgradePages.upcycle
      ) {
        items[2].enabled = true;
        items[1].enabled = true;
        setVisitedUpgradePages((prev) => ({ ...prev, upcycle: true }));
      }
      // enable step 4 (accessories page) when upcycle is in cart
      if (
        (isUpcycleInCart && currentPage === UPGRADE_STEPS.ACCESSORIES) ||
        visitedUpgradePages.accessories
      ) {
        items[3].enabled = true;
        items[2].enabled = true;
        items[1].enabled = true;
        setVisitedUpgradePages((prev) => ({
          ...prev,
          accessories: true,
        }));
      }

      return items;
    }
    // join flow
    if (isJFStepsPage(currentPage)) {
      const items = [
        jfPaginationOptionsItems.join,
        jfPaginationOptionsItems.bands,
        jfPaginationOptionsItems.membership,
        jfPaginationOptionsItems.accessories,
      ];

      // enable step 2 (options page) when device + tier are in cart
      if (
        (isDeviceInCart && currentPage === JOIN_FLOW_STEPS.BANDS) ||
        visitedJFPages.bands
      ) {
        items[1].enabled = true;
        setVisitedJFPages((prev) => ({ ...prev, bands: true }));
      }
      // enable step 3 (membership page) when band is in cart
      if (
        currentPage === JOIN_FLOW_STEPS.MEMBERSHIP ||
        visitedJFPages.membership
      ) {
        items[2].enabled = true;
        items[1].enabled = true;
        setVisitedJFPages((prev) => ({ ...prev, membership: true }));
      }
      // enable step 4 (accessories page) when membership is in cart
      if (
        (isMembershipInCart && currentPage === JOIN_FLOW_STEPS.ACCESSORIES) ||
        visitedJFPages.accessories
      ) {
        items[3].enabled = true;
        items[2].enabled = true;
        items[1].enabled = true;
        setVisitedJFPages((prev) => ({ ...prev, accessories: true }));
      }

      return items;
    }
  }, [currentPage, cart.cartItemGroups, tier]);

  const cartCounter = useMemo(() => {
    const numItems = getCartItemsFromCartItemGroups(
      filterCartItemsForExperiment(cart.cartItemGroups, cartExperimentVariant),
    )
      .map((product) => product.quantity)
      .reduce((prev, curr) => prev + curr, 0);

    return numItems;
  }, [cart.cartItemGroups]);

  // show currency selector on landing page only if device is not in cart
  const showCountrySelector =
    currentPage === UPGRADE_STEPS.UPGRADE ||
    currentPage === UPGRADE_STEPS.INELIGIBLE ||
    currentPage === JOIN_FLOW_STEPS.JOIN;
  const showCart = [
    UPGRADE_STEPS.OPTIONS,
    JOIN_FLOW_STEPS.BANDS,
    JOIN_FLOW_STEPS.MEMBERSHIP,
    UPGRADE_STEPS.UPCYCLE,
    JOIN_FLOW_STEPS.ACCESSORIES,
    UPGRADE_STEPS.ACCESSORIES,
  ].includes(currentPage);

  const trackClick = (name: string, number: number): void => {
    trackAmplitudeEvent(AmplitudeEvents.ClickedHeader, { name, number });
  };

  const showOrderPageV2Header =
    currentPage === JOIN_FLOW_STEPS.ORDER ||
    currentPage === JOIN_FLOW_STEPS.RECEIPT ||
    currentPage === UPGRADE_STEPS.ORDER ||
    currentPage === UPGRADE_STEPS.RECEIPT;

  return (
    <Header
      cartCounter={cartCounter}
      showCart={showCart}
      showCountrySelector={showCountrySelector}
      showOrderPageV2Header={showOrderPageV2Header}
      toggleIsCartOpen={toggleIsCartOpen}
    >
      {showPagination ? (
        <div className='absolute left-[50vw] flex -translate-x-1/2 justify-center'>
          <Pagination
            activeIndex={0}
            items={paginationOptions ?? []}
            trackHeaderClick={trackClick}
          />
        </div>
      ) : null}
    </Header>
  );
}

export default PaginationHeaderV2;
